<template>
	<div :class="tabsClasses">
		<TabNavigation
			:class="[bemm('nav'), props.tabNavClasses, props.tabNavWrapperClasses]"
			:items="tabItems"
			:value="activeNavigationId"
			:vertical="props.vertical"
			:centered="props.centered"
			:scroll-on-select="props.scrollOnSelect"
			@input="activateByNavigationId"
		/>

		<Card
			:type="props.contentCardType"
			:class="bemm('content-card')"
			:theme-color="props.themeColor"
		>
			<div :class="[bemm('content'), props.tabContentClasses]">
				<slot></slot>
			</div>
		</Card>
	</div>
</template>

<script lang="ts" setup>
import { computed, type PropType } from 'vue';
import { useBemm } from 'bemm';
import { Card, CardType } from '../Card';
import { Color } from '@arritech/shared/types';
import type { TabsClassValue, TabsRootEmit } from './Tabs.model';
import type { ButtonVariant } from '../Button';
import type { BaseCardProps } from '../Card/Card.model';
import TabNavigation from './TabNavigation.vue';
import { useTabsRoot } from './useTabs';

defineOptions({
	name: 'ArTabs',
});

const props = defineProps({
	type: {
		type: String,
		default: '',
	},
	activeTab: {
		type: String,
		default: '',
	},
	value: {
		type: String,
		default: '',
	},
	tabNavWrapperClasses: {
		type: [String, Object] as PropType<TabsClassValue>,
		default: '',
	},
	tabNavClasses: {
		type: [String, Object] as PropType<TabsClassValue>,
		default: '',
	},
	tabContentClasses: {
		type: [String, Object] as PropType<TabsClassValue>,
		default: '',
	},
	vertical: {
		type: Boolean,
		default: false,
	},
	centered: {
		type: Boolean,
		default: false,
	},
	scrollOnSelect: {
		type: Boolean,
		default: false,
	},
	activeVariant: {
		type: String as PropType<ButtonVariant>,
		default: 'default',
	},
	inactiveVariant: {
		type: String as PropType<ButtonVariant>,
		default: 'ghost',
	},
	color: {
		type: String as PropType<Color>,
		default: Color.SECONDARY,
	},
	themeColor: {
		type: String as PropType<Color>,
		default: null,
	},
	contentCardType: {
		type: String as PropType<BaseCardProps['type']>,
		default: CardType.OUTLINE,
	},
});

const emit = defineEmits<TabsRootEmit>();

const bemm = useBemm('ar-tabs', {
	return: 'string',
	includeBaseClass: true,
});

const { tabItems, activeNavigationId, activateByNavigationId } = useTabsRoot(
	props,
	emit
);

const tabsClasses = computed(() =>
	bemm('', {
		vertical: props.vertical,
		centered: props.centered,
	})
);
</script>

<style lang="scss">
.ar-tabs {
	$b: &;
	display: flex;
	flex-direction: column;
	gap: var(--space-s);

	&--vertical {
		flex-direction: row;
		align-items: flex-start;
	}

	&--centered {
		#{$b}__nav {
			justify-content: center;
		}
	}

	&__nav {
		width: fit-content;
	}

	&--vertical &__nav {
		width: auto;
	}

	&__content-card {
	}

	&__content {
		width: 100%;
	}
}

.ar-card__body:has(> .ar-tabs) {
	position: relative;
}
.ar-card__body > .ar-tabs:has(> .ar-tab-nav) {
	padding-top: var(--space);
}
.ar-card__body > .ar-tabs > .ar-tab-nav {
	position: absolute;
	top: 0;
	left: 50%;
	transform: translateY(-50%) translateX(-50%);
	background-color: var(--color-background);
	z-index: 1;
}
</style>
